/* eslint-disable camelcase */
import _, { isEmpty } from 'lodash';
import * as Yup from 'yup';
import { useSelector } from 'react-redux';
import { getFilteredPermissionData } from '.';
import {
  DATE_FORMAT,
  DATE_FORMAT_WITH_TIME,
  ROLES_PERMISSION_LIST,
  SUPPORTED_IMAGE_FORMATS,
} from '../constants/common';
import { selectAuthRoutesList } from '../pages/configuration/routeSlice';
import { PermissionType, type NavRoutePermissionListType, type OptionType } from '../types/common';
import { hasCapTableInstrumentImportAccess } from './permission/capTableInstrumentImportPermission';
import moment from 'moment';
import {
  Wealthlane_CustomerService_Dtos_CustomerUser_CustomerUserDto,
  type Wealthlane_CustomerService_Dtos_UserDefinedFields_UserDefinedFieldsByModuleIdDto,
} from '@wealthlane/api-services/wealthlane-customer-service';
import { timezones } from '../constants/timezones';
import { ACCEPT_DECIMAL, CURRENCY_DECIMAL } from './apiDecimalConfig';

export const getFormData = (data: any): any => {
  const formData = new FormData(); // Must be FormData so that the ajax request will be Form post
  Object.keys(data).forEach((k) => {
    formData.append(k, data[k]);
  });
  return formData;
};
//get default option from dropdown option
// export const getDefaultOption = (value?: string | boolean | number, options?: OptionType[]) => {
//   const defaultLabel = options?.find((item) => item?.value === value);
//   return defaultLabel;
// };

export const getActionList = (data: any[]): any[] => {
  const actionList = data?.map((item: any) => {
    return { ...item, action: item?.action, type: item?.action?.props?.children ?? item?.action };
  });

  return actionList;
};

export const checkNaNValue = (values: string | number, name?: string, setFieldName?: any) => {
  const checkValue = Number(values);
  if (!isNaN(checkValue)) {
    setFieldName && setFieldName(name, checkValue);
    return checkValue;
  } else {
    return '';
  }
};

export const setFormikFieldToNull = (
  key: string,
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void
): any => {
  setFieldValue(key, '');
  return null;
};

export const CommaFormatted = (
  amount: any,
  name?: string,
  setFieldValue?: any,
  ...rest: any
): string => {
  const numberValue = Number(String(amount)?.replaceAll(',', '') ?? '');

  // Check if the number has decimal places
  const hasDecimal = numberValue % 1 !== 0;
  const value = hasDecimal ? numberValue.toFixed(ACCEPT_DECIMAL) : numberValue.toLocaleString();

  if (name != null && Boolean(amount)) setFieldValue(name, !isNaN(numberValue) ? value : '');
  return value;
};

export const CommaFormattedWithDecimal = (
  amount: any,
  name?: string,
  setFieldValue?: any,
  ...rest: any
): string => {
  const numberValue = Number(String(amount)?.replaceAll(',', '') ?? '');
  const value = numberValue.toLocaleString(undefined, {
    minimumFractionDigits: ACCEPT_DECIMAL,
    maximumFractionDigits: ACCEPT_DECIMAL,
  });
  if (name != null && Boolean(amount)) setFieldValue(name, !isNaN(numberValue) ? value : '');
  return value;
};

export const CommaFormattedDecimal = (
  amount: any,
  name?: string,
  setFieldValue?: any,
  fixValue?: number
): any => {
  const numberValue = amount;
  let value = numberValue.toLocaleString(undefined, {
    maximumFractionDigits: fixValue ?? ACCEPT_DECIMAL,
  });
  // Check if the number is a whole number (no decimal part)
  if (Number.isInteger(numberValue)) {
    // Append .0000 to indicate it's a whole number
    value += '.0000';
  }
  if (name != null && Boolean(amount)) setFieldValue(name, value);
  return value;
};

/**
 * Format currency values with exactly 2 decimal places
 * Used for displaying monetary values like taxes, fees, prices, etc.
 * @param amount - The currency amount to format
 * @param name - Optional field name for Formik setFieldValue
 * @param setFieldValue - Optional Formik setFieldValue function
 * @param withCommas - Whether to format with commas (default: false)
 * @returns Formatted currency string with 2 decimal places
 */
export const FormatCurrency = (
  amount: any,
  name?: string,
  setFieldValue?: any,
  withCommas: boolean = false
): string => {
  const numberValue = Number(String(amount)?.replaceAll(',', '') ?? '');

  if (isNaN(numberValue)) {
    return '';
  }

  const fixedValue = numberValue.toFixed(CURRENCY_DECIMAL);

  let formattedValue = fixedValue;

  // Add comma formatting if requested
  if (withCommas) {
    const parts = fixedValue.split('.');
    const integerPart = parts[0];
    const decimalPart = parts[1];

    let formattedInteger = integerPart;
    const rgx = /(\d+)(\d{3})/;
    while (rgx.test(formattedInteger)) {
      formattedInteger = formattedInteger.replace(rgx, '$1' + ',' + '$2');
    }

    formattedValue = formattedInteger + '.' + decimalPart;
  }

  if (name != null && Boolean(amount)) {
    setFieldValue(name, formattedValue);
  }

  return formattedValue;
};

/**
 * Format currency input while user is typing
 * Adds commas for thousands but preserves decimal input as user types
 * Used in onChange handlers for currency input fields
 * @param value - The input value from the field
 * @param name - Field name for Formik setFieldValue
 * @param setFieldValue - Formik setFieldValue function
 * @returns Formatted value with commas
 */
export const FormatCurrencyInput = (value: any, name?: string, setFieldValue?: any): string => {
  const cleanValue = String(value)?.replaceAll(',', '') ?? '';

  if (isNaN(Number(cleanValue))) {
    return '';
  }

  const splitValue = String(value)?.split('.');
  const integerPart = String(splitValue[0])?.replaceAll(',', '') ?? '';
  const decimalPart = splitValue[1];

  // Format integer part with commas
  const formattedInteger = parseInt(integerPart || '0').toLocaleString();

  // Combine with decimal part if it exists
  const formattedValue = String(value).includes('.')
    ? `${formattedInteger}.${typeof decimalPart === 'string' ? decimalPart : ''}`
    : formattedInteger;

  if (name != null && setFieldValue) {
    setFieldValue(name, formattedValue);
  }

  return formattedValue;
};

export const CommaFormattedFloatPost = (amount: any, name?: string, setFieldValue?: any): any => {
  const removeComma = String(amount)?.replaceAll(',', '') ?? '';
  if (!isNaN(Number(removeComma))) {
    const numberValue = amount;
    const splitValue = amount?.split('.');
    const decimalValueWithComma = parseInt(
      String(splitValue[0])?.replaceAll(',', '') ?? ''
    ).toLocaleString();
    const value =
      String(amount).includes('.') && name == null
        ? numberValue
          .toFixed(ACCEPT_DECIMAL)
          .toLocaleString(undefined, { maximumFractionDigits: ACCEPT_DECIMAL })
        : numberValue.toLocaleString(undefined, { maximumFractionDigits: ACCEPT_DECIMAL });

    if (name != null && Boolean(amount))
      setFieldValue(
        name,
        amount.includes('.') === true
          ? `${decimalValueWithComma}.${typeof splitValue[1] === 'string' ? splitValue[1] : ''}`
          : decimalValueWithComma
      );
    return value;
  } else {
    setFieldValue(name, '');
  }
};

export const PureCommaFormattedFloatPost = (amount: any): any => {
  const removeComma = String(amount)?.replaceAll(',', '') ?? '';
  if (!isNaN(Number(removeComma))) {
    const numberValue = amount;
    const splitValue = amount?.split('.');
    const decimalValueWithComma = parseInt(
      String(splitValue[0])?.replaceAll(',', '') ?? ''
    ).toLocaleString();

    const value =
      String(amount).includes('.') && name == null
        ? numberValue
          .toFixed(ACCEPT_DECIMAL)
          .toLocaleString(undefined, { maximumFractionDigits: ACCEPT_DECIMAL })
        : numberValue.toLocaleString(undefined, { maximumFractionDigits: ACCEPT_DECIMAL });

    if (name != null && Boolean(amount))
      return amount.includes('.') === true
        ? `${decimalValueWithComma}.${typeof splitValue[1] === 'string' ? splitValue[1] : ''}`
        : decimalValueWithComma;
    return value;
  } else {
    return amount;
  }
};

export const getFileImage = (value: any): any => {
  const fileExtension = getFileExtension(value.file.name);
  if (fileExtension === 'pdf') {
    // return PdfImage;
  } else if (fileExtension === 'doc' || fileExtension === 'docx') {
    // return DocImage;
  } else if (SUPPORTED_IMAGE_FORMATS.includes(fileExtension)) {
    return value.src;
  } else {
    // return FileImage;
  }
};

export const getNonUsCountryValidation = (
  isOtherCountry: boolean | undefined,
  fieldName: string
): any => {
  if (isOtherCountry ?? false) {
    return Yup.string()
      .matches(/^[a-zA-Z ]*$/, `Invalid ${fieldName} Name`)
      .matches(/^\s*\S[\s\S]*$/, 'Must not contain blank spaces')
      .nullable();
    // return Yup.string()
    //   .required('Required field')
    //   .matches(/^[a-zA-Z ]*$/, `Invalid ${fieldName} Name`)
    //   .matches(/^\s*\S[\s\S]*$/, 'Must not contain blank spaces')
    //   .nullable();
  } else {
    return Yup.string()
      .matches(/^[a-zA-Z ]*$/, `Invalid ${fieldName} Name`)
      .matches(/^\s*\S[\s\S]*$/, 'Must not contain blank spaces')
      .nullable();
  }
};

// Get US Validation
export const getUsCountryValidation = (isCountryUS: boolean | undefined): any => {
  if (!(isCountryUS ?? false)) {
    return Yup.string().required('Required field').nullable();
  } else {
    return Yup.string().nullable();
  }
};

export const setObjectNullValue = (data: any): any => {
  const nullableData = Object.fromEntries(
    Object.entries(data).map(([k, v]) => [k, v === false ? false : v ?? null])
  );
  return nullableData;
};

// Set setArrayOfObjectNullValue
export const setArrayOfObjectNullValue = (arrData: any[]): any => {
  const nullableArrayData = arrData.map((item: any) =>
    Object.fromEntries(Object.entries(item).map(([k, v]) => [k, v === false ? false : v ?? null]))
  );
  return nullableArrayData;
};

export const getFileExtension = (name: string): string => {
  const fileArray = name.split('.');
  const fileExtension = fileArray[fileArray.length - 1];

  return fileExtension;
};

export const toFourDecimalPlaces = (value: number | string): string => {
  // Handle empty, null, undefined, or non-numeric values
  if (value === null || value === undefined || value === '') {
    return '';
  }

  // Convert to number if it's a string
  const numericValue = typeof value === 'string' ? parseFloat(value) : value;

  // Check if the conversion resulted in a valid number
  if (isNaN(numericValue)) {
    return '';
  }

  // Convert to 4 decimal places
  return numericValue.toFixed(ACCEPT_DECIMAL);
};

export const CommaFormattedFloat = (amount: any, name?: string, setFieldValue?: any): any => {
  const numberValue = amount;
  const value =
    amount.toString().includes('.') === true
      ? numberValue
        .toFixed(ACCEPT_DECIMAL)
        .toLocaleString(undefined, { maximumFractionDigits: ACCEPT_DECIMAL })
      : numberValue.toLocaleString(undefined, { maximumFractionDigits: ACCEPT_DECIMAL });

  if (name != null && Boolean(amount)) setFieldValue(name, value);
  return value;
};

export const setDashAsEmptyValue = (
  values: string | number | null | undefined
): string | number => {
  return values === null || values === '' || values === undefined ? '-' : values;
};

export const phoneFormat = (name: string, setFieldValue: any, value: string): void => {
  if (value === '') setFieldValue(name, value);
  const phoneNumber = value.replaceAll(/[^\d]/g, '');
  const phoneNumberLength = phoneNumber.length;
  if (phoneNumberLength < 4) {
    setFieldValue(name, phoneNumber);
  } else if (phoneNumberLength < 7) {
    setFieldValue(name, `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3)}`);
  } else {
    setFieldValue(
      name,
      `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`
    );
  }
};

export const phoneFormatWithDash = (value: string): string => {
  const phoneNumber = value?.replaceAll(/[^\d]/g, '');
  const formattedPhone = `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3, 6)}-${phoneNumber.slice(
    6,
    10
  )}`;
  return formattedPhone;
};
export const taxFormatWithDash = (value: string): string => {
  const taxNumber = value?.replace(/[^\d]/g, '');
  const formattedTax = `${taxNumber.slice(0, 2)}-${taxNumber.slice(2, 9)}`;
  return formattedTax;
};

export const ssnFormatWithDash = (name: string, setFieldValue: any, value: string): void => {
  if (value === '') setFieldValue(name, value);
  const ssnNumber = value.replaceAll(/[^\d]/g, '');
  const ssnNumberLength = ssnNumber.length;

  if (ssnNumberLength < 4) {
    setFieldValue(name, ssnNumber);
  } else if (ssnNumberLength < 6) {
    setFieldValue(name, `${ssnNumber.slice(0, 3)}-${ssnNumber.slice(3)}`);
  } else {
    setFieldValue(
      name,
      `${ssnNumber.slice(0, 3)}-${ssnNumber.slice(3, 5)}-${ssnNumber.slice(5, 9)}`
    );
  }
};
export const ssnDisplayFormat = (values: string, display: string): string => {
  const filteredValue = display.includes('show')
    ? `${values.slice(0, 3)}-${values.slice(3, 5)}-${values.slice(5, 9)}`
    : `XXX-XX-${values.slice(5, 9)}`;
  return filteredValue;
};

export const ssnWithDash = (value: string, view: string): string => {
  const ssnNumber = value?.replaceAll(/[^\d]/g, '');
  const formattedPhone =
    view === 'show'
      ? `${ssnNumber.slice(0, 3)}-${ssnNumber.slice(3, 5)}-${ssnNumber.slice(5, 9)}`
      : `XXX-XXX-${ssnNumber.slice(5, 9)}`;
  return formattedPhone;
};

export const NavPermissionCheck = (
  routeList: NavRoutePermissionListType[],
  permissionInfo: string
): any => {
  const userAuthList = useSelector(selectAuthRoutesList);
  const { nonFieldLevelPermissionsGroups } = getFilteredPermissionData(
    userAuthList?.auth?.grantedPolicies ?? {},
    permissionInfo
  );
  const hasPermission = Object.keys(nonFieldLevelPermissionsGroups)?.map((item: string) => item);

  const filterPermissionList = routeList.filter((item: NavRoutePermissionListType) => {
    if (item.capTableInstrumentImport) {
      return hasCapTableInstrumentImportAccess(userAuthList?.auth?.grantedPolicies);
    }
    if (Array.isArray(item.permissionName)) {
      return item.permissionName.some((name) => hasPermission.includes(name));
    }
    if (item.permissionName === '') {
      return false;
    }
    return hasPermission.includes(item.permissionName);
  });

  return filterPermissionList;
};

export const downloadBlobFile = (blob: Blob, fileName: string): void => {
  const blobUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(blobUrl);
};

export const handleOpenFile = (fileName: string, fileContent: string): any => {
  const fileExtension = getFileExtension(fileName);

  if (SUPPORTED_IMAGE_FORMATS.includes(fileExtension)) {
    const image = new Image();
    image.src = `data:image/${fileExtension};base64,` + fileContent;
    const w: any = window.open('');
    w.document.write(image.outerHTML);
    w.document.close();
  } else if (fileExtension === 'pdf') {
    // debugger;
    // console.log('name', fileName);
    // console.log('content', fileContent);

    const pdfWindow: any = window.open('');
    pdfWindow.document.write(
      `<iframe width='100%' height='100%' src='data:application/pdf;base64, ` +
      encodeURI(fileContent) +
      // fileContent +
      "'></iframe>"
    );
  } else {
    const aElement = document.createElement('a');
    const downloadLink = `data:application/${fileExtension};base64,` + fileContent;
    aElement.setAttribute('download', fileName);
    aElement.setAttribute('href', downloadLink);
    aElement.click();
  }
};

export const handleDownloadFile = (fileName: string, fileContent: string): void => {
  const fileExtension = getFileExtension(fileName);
  const aElement = document.createElement('a');
  const downloadLink = `data:application/${fileExtension};base64,` + fileContent;
  aElement.setAttribute('download', fileName);
  aElement.setAttribute('href', downloadLink);
  aElement.click();
};

export const getPopoverGridSystemRow = (data: any[]): number => {
  const dataLength = _.range(1, 11);
  let row: number = 1;
  dataLength.forEach((item) => {
    if (data.length >= item * 5) {
      row = row + 1;
    }
  });
  return row;
};

export const getPopoverGridSystemColumn = (data: any[]): 1 | 2 | 3 | 4 | undefined => {
  if (data.length <= 2) {
    return 1;
  } else if (data.length <= 4) {
    return 2;
  } else if (data.length <= 6) {
    return 3;
  } else if (data.length > 6) {
    return 4;
  }
};

export const handleFilterPermissionList = (
  data: Record<
    string,
    Array<{
      groupKey: string;
      key: string;
      text: string;
    }>
  >
): any => {
  const rolesObj: any = {};
  const permissionInfo = 'CustomerService.Customer.';
  Object.entries(data).forEach(([key]) => {
    const rolesAllData = data[`${key}`].map((itemData) => itemData.text);
    const newData: any = data[`${key}`];
    ROLES_PERMISSION_LIST.forEach((item) => {
      if (!rolesAllData.includes(item)) {
        newData.push({
          groupKey: data[`${key}`][0].groupKey,
          key: `${permissionInfo}${data[`${key}`][0].groupKey}.${item}`,
          text: item,
          isDisabled: true,
        });
      }
    });

    const filteredRoles = newData.sort((a: any, b: any) => {
      const A = a.text;
      const B = b.text;
      return ROLES_PERMISSION_LIST.indexOf(A) > ROLES_PERMISSION_LIST.indexOf(B) ? 1 : -1;
    });

    rolesObj[key] = filteredRoles;
  });
  return rolesObj;
};

export const handleSortPermissionNames = (data: PermissionType, filterList: string[]): any => {
  const sortedData = Object.keys(data)
    .sort((a: string, b: string) => {
      return filterList.indexOf(a) > filterList.indexOf(b) ? 1 : -1;
    })
    .reduce((result: PermissionType, key: string) => {
      result[key] = data[key];
      return result;
    }, {});

  return sortedData;
};

export const convertNumberWithCommas = (
  value?: number | string,
  showDecimal?: boolean,
  fixValue?: number
): any => {
  const NumberValue = Number(value)?.toFixed(ACCEPT_DECIMAL);
  value = `${String(value) ?? ''}`;
  const x: any = NumberValue?.toString().split('.');
  let x1 = x[0];
  const x2 =
    /* x.length > 1 ? '.' + Number( */ '.' +
    String(x[1] ?? ''); /* ).toFixed(ACCEPT_DECIMAL) : '' */
  const rgx = /(\d+)(\d{3})/;
  while (rgx.test(x1)) {
    // eslint-disable-next-line no-useless-concat
    x1 = x1.replace(rgx, '$1' + ',' + '$2');
  }
  const finalValue = showDecimal ?? false ? String(x1 ?? '') + String(x2 ?? '') : x1;

  return finalValue;
};

export const twoDecimalNumberWithoutRounds = (value: number | string, showDecimal?: boolean) => {
  const numStr = value.toString();
  const splitNumber = numStr.split('.');

  // Ensure decimal places without rounding based on ACCEPT_DECIMAL
  const decimalPart = splitNumber[1]
    ? splitNumber[1].substring(0, ACCEPT_DECIMAL)
    : '0'.repeat(ACCEPT_DECIMAL);
  const result = splitNumber[0] + '.' + decimalPart.padEnd(ACCEPT_DECIMAL, '0');

  const x = result.split('.');
  let x1 = x[0];
  const x2 = '.' + (x[1] ?? '0'.repeat(ACCEPT_DECIMAL));

  // Regex to add commas for thousands separators
  const rgx = /(\d+)(\d{3})/;
  while (rgx.test(x1)) {
    x1 = x1.replace(rgx, '$1' + ',' + '$2');
  }

  // Determine the final value based on the showDecimal flag
  const finalValue = showDecimal ? `${x1}${x2}` : x1;
  return finalValue;
};

export const toDecimalForReports = (value: number | string) => {
  // Handle empty, null, or undefined values
  if (!value && value !== 0) return '';

  const numStr = value.toString();
  const splitNumber = numStr.split('.');

  // For whole numbers (no decimal part)
  if (!splitNumber[1]) {
    return `${splitNumber[0]}.${'0'.repeat(ACCEPT_DECIMAL)}`;
  }

  // Truncate decimal part to ACCEPT_DECIMAL places without rounding
  const decimalPart = splitNumber[1].substring(0, ACCEPT_DECIMAL).padEnd(ACCEPT_DECIMAL, '0');

  return `${splitNumber[0]}.${decimalPart}`;
};

export const MaterialIconFilled = {
  fontVariationSettings: `"FILL" 1, "wght" 400, "GRAD" 0, "opsz" 48`,
};

export const changeTimeZone = (date: any, timeZone: string): any => {
  if (typeof date === 'string') {
    return moment(
      new Date(date)?.toLocaleString('en-US', {
        timeZone,
      })
    ).format(DATE_FORMAT_WITH_TIME);
  }

  return moment(
    date?.toLocaleString('en-US', {
      timeZone,
    })
  ).format(`${DATE_FORMAT} . h:mm A`);
};

export const getTzAbbr = function (dateInput?: any) {
  var dateObject = dateInput || new Date(),
    dateString = dateObject + '',
    tzAbbr =
      // Works for the majority of modern browsers
      dateString.match(/\(([^\)]+)\)$/) ||
      // IE outputs date strings in a different format:
      dateString.match(/([A-Z]+) [\d]{4}$/);

  if (tzAbbr) {
    // Old Firefox uses the long timezone name (e.g., "Central
    // Daylight Time" instead of "CDT")
    tzAbbr = tzAbbr[1].match(/[A-Z]/g).join('') as any;
  }

  // Uncomment these lines to return a GMT offset for browsers
  // that don't include the user's zone abbreviation (e.g.,
  // "GMT-0500".) I prefer to have `null` in this case, but
  // you may not!
  // First seen on: http://stackoverflow.com/a/12496442
  // if (!tzAbbr && /(GMT\W*\d{4})/.test(dateString)) {
  // 	return RegExp.$1;
  // }

  return tzAbbr;
};

export function getTimeZone() {
  const tzAbr = getTzAbbr();
  if (tzAbr) return tzAbr;
  const currentOffset = new Date().getTimezoneOffset();
  const offsetInHours = -currentOffset / 60;
  return timezones.find((tz) => tz.offset === offsetInHours)?.abbr;
}

export function convertToCurrentTimezone(inputDate: any) {
  let dateObj = inputDate;
  if (typeof inputDate === 'string') {
    dateObj = new Date(inputDate);
  }
  return moment(dateObj).format(`${DATE_FORMAT_WITH_TIME}`);
}

export const reportDetailFunction = (response: Record<string, any>, formStatus: string): any => {
  const reportDetail =
    formStatus === 'Edit'
      ? {
        taskId: response.taskId,
        detailIds: [response?.detailId],
      }
      : {
        taskId: response[0].taskId,
        detailIds: response?.map((data: any) => data?.detailId),
      };
  return reportDetail;
};
export const getUdfModuleId = (
  options: OptionType[] | undefined,
  modulePage: number | string
): any => {
  return options?.find((option: OptionType) => option.value === modulePage)?.value;
};

export const udfPayloadFiltered = (
  formData: Record<string, any>,
  response: Record<string, any>,
  UdfFields:
    | Wealthlane_CustomerService_Dtos_UserDefinedFields_UserDefinedFieldsByModuleIdDto[]
    | any,
  UdfValueResponse: Array<Record<string, string>>,
  moduleId: number
): any => {
  const udfWithoutValue = UdfFields?.filter(
    (value: any) => !UdfValueResponse?.some((value2: any) => value.id === value2.userDefinedFieldId)
  );
  const udfWithValue = UdfValueResponse.filter((value: any) =>
    UdfFields.some((value2: any) => value.userDefinedFieldId === value2.id)
  );
  const filteredData = !isEmpty(udfWithoutValue)
    ? formData?.flatMap((item: any, index: number) =>
      UdfFields?.map((udfFields: any) => {
        const fieldName = udfFields?.fieldName?.replaceAll(' ', '');
        const udfName: any =
          Boolean(fieldName) &&
          String(fieldName.charAt(0).toLowerCase() ?? '') + String(fieldName.slice(1) ?? '');
        const udfPayloadWithoutValue = Boolean(udfWithoutValue) && {
          moduleId: moduleId,
          participantId: response?.participantId ?? response[index]?.participantId,

          userDefinedFieldId: !isEmpty(udfWithoutValue)
            ? udfWithoutValue?.find((udfValue: any) => udfValue.id === udfFields.id)?.id
            : udfFields.id,
          moduleFormId: response?.id ?? response[index]?.id,
          value: item[udfName] != null ? item[udfName] : '',
        };
        return udfPayloadWithoutValue;
      })
    )
    : [];
  const filteredData2 = !isEmpty(udfWithValue)
    ? formData?.flatMap((item: any, index: number) =>
      UdfFields?.map((udfFields: any) => {
        const fieldName = udfFields?.fieldName?.replaceAll(' ', '');
        const udfName: any =
          Boolean(fieldName) &&
          String(fieldName.charAt(0).toLowerCase() ?? '') + String(fieldName.slice(1) ?? '');
        const udfPayloadWithValue = {
          moduleFormId: response?.id,
          id: item[`${String(udfName ?? '')}Id`],
          value: item[udfName] != null ? item[udfName] : '',
        };
        return udfPayloadWithValue;
      })
    )
    : [];
  const udfPayloadWithValue = !isEmpty(filteredData)
    ? filteredData?.filter((item: any) => item.userDefinedFieldId !== undefined)
    : [];

  const udfPayloadWithoutFormId = !isEmpty(filteredData2)
    ? [...filteredData2]?.filter((item: any) => item.id !== undefined)
    : [];

  return { udfPayloadWithValue, udfPayloadWithoutFormId };
};

export const capitalizeFirstLetter = (str: string): string => {
  return str?.charAt(0).toUpperCase() + str.slice(1);
};

export const calculateAvailableShares = (totalShares: number, availableShares: number): number => {
  const shareAvailableInPercent = ((availableShares / totalShares) * 100).toFixed(ACCEPT_DECIMAL);
  return Number(shareAvailableInPercent);
};

export const handleStringCheck = (
  value: string,
  setCheck: ({
    longer,
    lowerCase,
    upperCase,
    isNumber,
    hasSymbol,
  }: {
    longer: boolean;
    lowerCase: boolean;
    upperCase: boolean;
    isNumber: boolean;
    hasSymbol: boolean;
  }) => void
): any => {
  const longer = value.length >= 8;
  const isNumber = /\d/.test(value);
  // eslint-disable-next-line no-useless-escape
  const hasSymbol = /[!@$%&*?]/.test(value);
  const lowerCase = /[a-z]/.test(value);
  const upperCase = /[A-Z]/.test(value);

  setCheck({
    longer,
    lowerCase,
    upperCase,
    isNumber,
    hasSymbol,
  });
};

export const UniqueArrayCheck = (array1: string[], array2: string[]) => {
  const combinedArray = array1.concat(array2);
  const uniqueArray: string[] = [];

  combinedArray.forEach((item, i) => {
    if (!uniqueArray.includes(combinedArray[i])) {
      uniqueArray.push(combinedArray[i]);
    }
  });

  return uniqueArray;
};

export const areArraysEqual = (arr1: string[], arr2: string[]) => {
  if (arr1.length !== arr2.length) {
    return false;
  }

  const sortedArr1 = arr1.slice().sort();
  const sortedArr2 = arr2.slice().sort();

  return sortedArr1.every((value, index) => value === sortedArr2[index]);
};

export const convertToXXX = (values: string): string => {
  const value = values.replaceAll(/./g, 'X');
  return value;
};

export const TaxIdFormat = (name: string, value: string, setFieldValue?: any): string => {
  if (value !== '' && setFieldValue) setFieldValue(name, value);
  const taxId = value.replace(/[^\d]/g, '');
  const taxIdLength = taxId.length;
  let finalValue = '';
  if (taxIdLength < 3) {
    setFieldValue(name, taxId);
    finalValue = taxId;
  } else {
    finalValue = `${taxId.slice(0, 2)}-${taxId.slice(2, taxIdLength)}`;
    if (setFieldValue) setFieldValue(name, finalValue);
  }
  return finalValue;
};

export const getHiddenIndexes = (data: any, key: string = 'parameters'): number[] => {
  if (!data?.[key]) return [];

  return data?.[key]
    .map((param: any, index: number) => (param.isHidden ? index : -1))
    .filter((index: number) => index !== -1);
};

export const getNonRequiredParameterIndexes = (data: any, key: string = 'parameters'): number[] => {
  if (!data?.[key]) return [];

  return data[key]
    .map((param: any, index: number) =>
      param.isHidden || String(param.isMandatory).toLowerCase() === 'false' ? index : -1
    )
    .filter((index: number) => index !== -1);
};

export const changeArrayValues = (
  array: Record<string, string | number | boolean>[],
  rowId: string,
  name: string,
  value: string | number | boolean
): any => {
  const filter: any = array?.find((item: any) => item?.participantId || item?.awardId === rowId);
  filter[`${name}`] = value;
  const existingIndex = array.findIndex(
    (item: any) => (item?.id || item?.awardId || item?.openLotId || item?.participantId) === rowId
  );
  // If there is an overlap, replace the existing data with the new data
  array[existingIndex] = filter;
  return [...array];
};

// Helper function to convert strings to camelCase
export const toCamelCase = (str: string): string => {
  return str
    ?.replace(/[^a-zA-Z0-9\s]/g, '') // Remove special characters
    ?.split(/\s+/) // Split by spaces
    ?.map((word, index) => {
      if (index === 0) {
        return word?.toLowerCase();
      }
      return word?.charAt(0)?.toUpperCase() + word?.slice(1)?.toLowerCase();
    })
    ?.join('');
};

export const getUserName = (
  user: Wealthlane_CustomerService_Dtos_CustomerUser_CustomerUserDto
): string => {
  const firstName = user?.firstName || '';
  const middleName = user?.middleName || '';
  const lastName = user?.lastName || '';

  return [firstName, middleName, lastName].filter((name) => name.trim() !== '').join(' ');
};

/**
 * Performance-optimized version with circular reference detection and depth limiting
 * Best for large API responses and deeply nested structures
 */
interface ConversionOptions {
  /** Maximum depth to traverse (prevents stack overflow) */
  maxDepth?: number;
  /** Mutate original object instead of creating new one (better memory usage) */
  inPlace?: boolean;
  /** Only process arrays and direct object properties, skip nested objects */
  shallow?: boolean;
  /** Array of field paths to EXCLUDE from conversion (keep raw values) */
  excludeFields?: string[];
}

export const convertAllDecimalsToFourPlacesOptimized = <T = any,>(
  data: T,
  options: ConversionOptions = {}
): T => {
  const { maxDepth = 50, inPlace = false, shallow = false, excludeFields = [] } = options;
  // Use WeakMap to detect circular references and avoid infinite loops
  const seen = new WeakMap<any, any>();

  // Helper to check if field path should be excluded (keep raw value)
  const shouldExclude = (path: string): boolean => {
    return excludeFields.some((pattern) => {
      if (pattern === path) return true;
      // Support wildcard patterns like 'items[].ratio'
      const regex = pattern.replace(/\[\]/g, '\\[\\d+\\]').replace(/\./g, '\\.');
      return new RegExp(`^${regex}$`).test(path);
    });
  };

  const convert = (value: any, currentDepth: number, currentPath: string = ''): any => {
    try {
      // Depth limit check - prevent stack overflow
      if (currentDepth > maxDepth) {
        return value;
      }

      // Handle null or undefined
      if (value === null || value === undefined) {
        return value;
      }

      // Handle numbers - convert to 4 decimal places (unless excluded)
      if (typeof value === 'number') {
        // Skip conversion for excluded fields
        if (currentPath && shouldExclude(currentPath)) {
          return value; // Keep raw decimal value
        }

        if (isNaN(value) || !isFinite(value)) {
          return value;
        }
        // Truncate to 4 decimal places without rounding
        // Use toFixed first to handle floating-point precision issues, then truncate
        const fixedStr = value.toFixed(5); // Get 5 decimal places first
        const truncatedStr = fixedStr.slice(0, fixedStr.indexOf('.') + 5); // Keep only 4 decimal places
        return parseFloat(truncatedStr);
      }

      // Handle strings - return as-is without conversion
      if (typeof value === 'string') {
        return value;
      }

      // Handle arrays
      if (Array.isArray(value)) {
        // Circular reference check
        if (seen.has(value)) {
          return seen.get(value);
        }

        const result = inPlace ? value : [];
        seen.set(value, result);

        for (let i = 0; i < value.length; i++) {
          const itemPath = currentPath ? `${currentPath}[${i}]` : `[${i}]`;
          result[i] = convert(value[i], currentDepth + 1, itemPath);
        }
        return result;
      }

      // Handle objects
      if (typeof value === 'object') {
        // Skip special objects (Date, RegExp, etc.)
        if (
          value instanceof Date ||
          value instanceof RegExp ||
          value instanceof Error ||
          value instanceof Map ||
          value instanceof Set
        ) {
          return value;
        }

        // Circular reference check
        if (seen.has(value)) {
          return seen.get(value);
        }

        const result = inPlace ? value : {};
        seen.set(value, result);

        // Recursively process all nested objects, arrays, and values
        for (const key in value) {
          if (Object.prototype.hasOwnProperty.call(value, key)) {
            const val = value[key];
            const fieldPath = currentPath ? `${currentPath}.${key}` : key;

            // Shallow mode: only convert direct numeric values, skip nested structures
            if (shallow && typeof val === 'object' && val !== null) {
              result[key] = val; // Skip nested objects/arrays in shallow mode
            } else {
              // Deep mode (default): recursively convert everything including nested structures
              result[key] = convert(val, currentDepth + 1, fieldPath);
            }
          }
        }
        return result;
      }

      // For any other type, return as is
      return value;
    } catch (error) {
      return value;
    }
  };

  return convert(data, 0, '') as T;
};
